import { useCallback, useEffect, useRef } from 'react';

export type SocketCloseReason = 'closed' | 'error';

interface Options<TIncoming> {
  /** Socket URL. `null` keeps the socket closed — used to open it on demand. */
  url: string | null;
  onOpen?: () => void;
  onMessage?: (msg: TIncoming) => void;
  /** Fired once, for whichever of error/close happens first. */
  onClose?: (reason: SocketCloseReason) => void;
}

/**
 * A JSON WebSocket bound to a component's lifetime.
 *
 * Callbacks are held in refs so that re-renders never tear the socket down —
 * only a change of `url` (or unmount) does. Closing from inside `onMessage`
 * (matchmaking hands off to the game server) is safe: `close()` detaches the
 * handlers first so no spurious "disconnected" state is reported.
 */
export function useJsonSocket<TIncoming, TOutgoing>({
  url,
  onOpen,
  onMessage,
  onClose,
}: Options<TIncoming>) {
  const socketRef = useRef<WebSocket | null>(null);
  const handlers = useRef({ onOpen, onMessage, onClose });
  handlers.current = { onOpen, onMessage, onClose };

  useEffect(() => {
    if (!url) return;

    const ws = new WebSocket(url);
    socketRef.current = ws;
    let settled = false;
    const settle = (reason: SocketCloseReason) => {
      if (settled) return;
      settled = true;
      handlers.current.onClose?.(reason);
    };

    ws.onopen = () => handlers.current.onOpen?.();
    ws.onmessage = (event) => {
      let parsed: TIncoming;
      try {
        parsed = JSON.parse(event.data as string) as TIncoming;
      } catch {
        console.error('[socket] could not parse message:', event.data);
        return;
      }
      handlers.current.onMessage?.(parsed);
    };
    ws.onerror = () => settle('error');
    ws.onclose = () => settle('closed');

    return () => {
      settled = true; // unmount/URL change is not a reportable disconnect
      ws.onopen = ws.onmessage = ws.onerror = ws.onclose = null;
      if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
        ws.close();
      }
      if (socketRef.current === ws) socketRef.current = null;
    };
  }, [url]);

  const send = useCallback((payload: TOutgoing) => {
    const ws = socketRef.current;
    if (ws?.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(payload));
    } else {
      console.warn('[socket] dropped message, socket not open:', payload);
    }
  }, []);

  /** Close without reporting a disconnect — for a deliberate hand-off. */
  const close = useCallback(() => {
    const ws = socketRef.current;
    if (!ws) return;
    ws.onopen = ws.onmessage = ws.onerror = ws.onclose = null;
    ws.close();
    socketRef.current = null;
  }, []);

  return { send, close };
}
